import { type HotelInventoryListItem } from '@utazzo/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';

/**
 * Month calendar showing the resolved adult/night price for one room - the
 * same lookup the itinerary picker does: for each day, the active period
 * whose date range contains it AND whose weekday mask includes that weekday.
 * Days with no matching period show blank; conflicting prices never occur
 * (the overlap guard prevents them).
 */

interface RatePriceCalendarProps {
  /** Periods for ONE room (already filtered by caller). */
  periods: HotelInventoryListItem[];
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const fmtMoney = (amount: number, currencyCode: string | null): string => {
  const code = currencyCode ?? 'INR';
  const prefix = code === 'INR' ? '₹' : `${code} `;
  return `${prefix}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

/** Resolve the headline price (adult, else fixed) for a given Date. */
const priceForDate = (
  periods: HotelInventoryListItem[],
  date: Date,
): { amount: number; currencyCode: string | null; blocked?: boolean } | null => {
  const ymd = date.toISOString().slice(0, 10);
  const dow = date.getUTCDay(); // 0=Sun..6=Sat
  for (const p of periods) {
    if (!p.isActive) continue;
    if (ymd < p.validityFrom || ymd > p.validityTo) continue;
    if ((p.weekdayMask & (1 << dow)) === 0) continue;
    // Sold-out / closed day within this period.
    if (p.blackoutDates?.includes(ymd)) {
      return { amount: 0, currencyCode: p.currencyCode, blocked: true };
    }
    const adult = p.adultPrice;
    const fixed = p.fixedRoomCost;
    if (adult > 0) return { amount: adult, currencyCode: p.currencyCode };
    if (fixed > 0) return { amount: fixed, currencyCode: p.currencyCode };
    return { amount: 0, currencyCode: p.currencyCode };
  }
  return null;
};

export const RatePriceCalendar = ({ periods }: RatePriceCalendarProps): React.JSX.Element => {
  const today = new Date();
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(Date.UTC(year, month, 1));
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    // Lead blanks so the 1st lands under its weekday (Mon-first grid).
    const firstDow = first.getUTCDay(); // 0=Sun
    const lead = (firstDow + 6) % 7; // convert to Mon=0
    const cells: ({ day: number; date: Date } | null)[] = [];
    for (let i = 0; i < lead; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({ day: d, date: new Date(Date.UTC(year, month, d)) });
    }
    return cells;
  }, [cursor]);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white">
      <header className="flex items-center justify-between border-b border-zinc-100 px-4 py-3">
        <h4 className="text-sm font-semibold tracking-tight text-zinc-900">
          {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </h4>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            aria-label="Previous month"
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
            className="rounded px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            aria-label="Next month"
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-7 gap-px border-b border-zinc-100 bg-zinc-50 px-3 pt-2 text-center text-[10px] font-medium tracking-wide text-zinc-400 uppercase">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="pb-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 p-3">
        {grid.map((cell, i) => {
          if (!cell) return <div key={`b${i}`} />;
          const price = priceForDate(periods, cell.date);
          const isWeekend = cell.date.getUTCDay() === 0 || cell.date.getUTCDay() === 6;
          return (
            <div
              key={cell.day}
              className={
                'flex min-h-[52px] flex-col rounded-md border p-1.5 ' +
                (price?.blocked
                  ? 'border-red-200 bg-red-50/60'
                  : price
                    ? 'border-zinc-200 bg-white'
                    : 'border-dashed border-zinc-200 bg-zinc-50/40')
              }
            >
              <span
                className={
                  'text-[10px] tabular-nums ' + (isWeekend ? 'text-amber-600' : 'text-zinc-400')
                }
              >
                {cell.day}
              </span>
              {price?.blocked ? (
                <span className="mt-auto text-[10px] font-medium text-red-500">Blocked</span>
              ) : price ? (
                <span className="mt-auto text-xs font-medium text-zinc-900 tabular-nums">
                  {price.amount > 0 ? fmtMoney(price.amount, price.currencyCode) : '-'}
                </span>
              ) : (
                <span className="mt-auto text-[10px] text-zinc-300">no price</span>
              )}
            </div>
          );
        })}
      </div>

      <footer className="flex items-center gap-4 border-t border-zinc-100 px-4 py-2 text-[11px] text-zinc-500">
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm border border-zinc-200 bg-white" /> priced
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm border border-dashed border-zinc-200 bg-zinc-50" />
          no price
        </span>
        <span className="text-amber-600">weekend dates in amber</span>
      </footer>
    </div>
  );
};
